import { useEffect, useMemo, useState } from "react";
import { IHttpClient } from "../../../lib/http";
import { CurrencyEntity } from "../../currency/domain/currency.entity";
import { getAllEntities } from "../../currency/infrastructure/currency.api";
import { ReportEntity } from "../domain/report.entity";
import { ReportType } from "../index";
import { dtoToEntity, ReportDTO, ReportResultDTO } from "./report.dto";

export interface KeywordFilter {
  value: string;
  columnsToFilter: string[];
}

export interface ReportRequest {
  hotelTickers: string[];
  checkIn: string;
  checkOut: string;
  concept: string;
  currency: string;
  dateSearchCriteria: string;
  groupByEntity: string;
  groupDateBy: string;
  isBookingByStatus: boolean;
  keywordFilter: KeywordFilter | null;
  orderBy: {
    [K in keyof Partial<ReportResultDTO>]: "asc" | "desc";
  };
  status: string[];
  statusConfig: number[];
  paymentTypes: string[];
}

export interface GetReportRequest {
  request: ReportRequest;
  size: number;
  page: number;
}

export interface ReportSummarizeColumns {
  type: ReportType;
  sumText: string;
  avgText: string;
}

export interface ReportResponse {
  report: ReportEntity[];
  totalResults: number;
}

async function getBookingsReport({
  request,
  httpClient,
  size,
  page,
  type,
  sumText,
  avgText,
}: GetReportRequest & ReportSummarizeColumns & { httpClient: IHttpClient }): Promise<
  ReportResponse
> {
  const reportDTO: ReportDTO = await httpClient.post<ReportRequest, ReportDTO>(
    `/api/report/bookings?size=${size}&page=${page}`,
    {
      ...request,
    }
  );
  return {
    report: dtoToEntity(reportDTO, type, sumText, avgText),
    totalResults: reportDTO.totalResults,
  };
}

export const useReportApi = (httpClient: IHttpClient) => {
  return useMemo(
    () => ({
      getBookingsReport: (props: GetReportRequest & ReportSummarizeColumns) =>
        getBookingsReport({ httpClient, ...props }),
    }),
    [httpClient]
  );
};

export function usePropertyCurrencies(): { currencies: string[]; defaultCurrency?: string } {
  const [currencies, setCurrencies] = useState<CurrencyEntity[]>();
  const defaultCurrency = useMemo(() => currencies?.find(it => it.primary)?.code, [currencies]);

  useEffect(() => {
    const fetchCurrencies = async () => {
      const currencies = await getAllEntities();
      setCurrencies(currencies);
    };
    fetchCurrencies().catch(e => console.error("Error fetching property currencies", e));
  }, []);

  return {
    currencies: currencies?.map(it => it.code) || [],
    defaultCurrency,
  };
}
